import type { Call } from 'features/call/call-base/interfaces';
import { CallDaoService } from 'features/call/call-base/services';
import { handleError } from 'features/notification/store';

type CallRecordUpdateDispatch = (action: unknown) => unknown;

/** Quiet period after the last queued update before sending the merged PATCH. */
export const UPDATE_CALL_RECORD_DEBOUNCE_MS = 500;

/**
 * Upper bound from the first queued update in a batch so overlapping callers
 * within about a second still collapse into one request.
 */
export const UPDATE_CALL_RECORD_MAX_WAIT_MS = 1000;

type CallRecordUpdateWaiter = (call: Call | undefined) => void;

type PendingCallRecordBatch = {
  payload: Partial<Call>;
  callId: number;
  waiters: CallRecordUpdateWaiter[];
  dispatch: CallRecordUpdateDispatch;
};

let pendingBatch: PendingCallRecordBatch | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let maxWaitTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight: Promise<Call | undefined> | null = null;

const clearBatchTimers = () => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }

  if (maxWaitTimer !== null) {
    clearTimeout(maxWaitTimer);
    maxWaitTimer = null;
  }
};

const snapshotAndClearPendingBatch = (): PendingCallRecordBatch | null => {
  const snapshot = pendingBatch;
  pendingBatch = null;
  clearBatchTimers();
  return snapshot;
};

const scheduleBatchTimers = () => {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
  }

  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void flushCallRecordUpdateBatch();
  }, UPDATE_CALL_RECORD_DEBOUNCE_MS);

  maxWaitTimer ??= setTimeout(() => {
    maxWaitTimer = null;
    void flushCallRecordUpdateBatch();
  }, UPDATE_CALL_RECORD_MAX_WAIT_MS);
};

const executeCallRecordUpdate = async (
  snapshot: PendingCallRecordBatch
): Promise<Call | undefined> => {
  if (Object.keys(snapshot.payload).length === 0) {
    snapshot.waiters.forEach((resolve) => resolve(undefined));
    return undefined;
  }

  try {
    const call = await CallDaoService.updateCall(
      snapshot.callId,
      snapshot.payload
    );
    snapshot.waiters.forEach((resolve) => resolve(call));
    return call;
  } catch (error) {
    snapshot.dispatch(handleError({ error, methodName: 'updateCallRecord' }));
    snapshot.waiters.forEach((resolve) => resolve(undefined));
    return undefined;
  }
};

export const flushCallRecordUpdateBatch = (): Promise<Call | undefined> => {
  const snapshot = snapshotAndClearPendingBatch();

  if (!snapshot) {
    return inFlight ?? Promise.resolve(undefined);
  }

  const previousFlight = inFlight;
  const promise = (async () => {
    if (previousFlight) {
      await previousFlight;
    }

    return executeCallRecordUpdate(snapshot);
  })();

  inFlight = promise;
  void promise.finally(() => {
    if (inFlight === promise) {
      inFlight = null;
    }
  });

  return promise;
};

const hasQueuedCallRecordUpdate = () =>
  pendingBatch !== null || inFlight !== null;

export const drainCallRecordUpdateQueue = async (): Promise<void> => {
  while (hasQueuedCallRecordUpdate()) {
    await flushCallRecordUpdateBatch();
  }
};

export const enqueueCallRecordUpdate = (
  payload: Partial<Call>,
  callId: number,
  dispatch: CallRecordUpdateDispatch
): Promise<Call | undefined> => {
  if (pendingBatch && pendingBatch.callId !== callId) {
    void flushCallRecordUpdateBatch();
  }

  return new Promise((resolve) => {
    if (!pendingBatch) {
      pendingBatch = {
        payload: { ...payload },
        callId,
        waiters: [resolve],
        dispatch,
      };
      scheduleBatchTimers();
      return;
    }

    pendingBatch = {
      ...pendingBatch,
      payload: { ...pendingBatch.payload, ...payload },
      waiters: [...pendingBatch.waiters, resolve],
      dispatch,
    };
    scheduleBatchTimers();
  });
};

export const resetCallRecordUpdateBatcher = () => {
  clearBatchTimers();
  pendingBatch?.waiters.forEach((resolve) => resolve(undefined));
  pendingBatch = null;
  inFlight = null;
};
